import React, { useState, useCallback, useEffect } from 'react';
import {
  Form,
  Button,
  Icon,
  Segment,
  Header,
  Checkbox,
  Label,
  Message,
  Loader,
  Divider,
} from 'semantic-ui-react';

import Config from '../../../constants/Config';

const ALL_COLUMNS = [
  { key: 'id', label: 'ID (link to RCM)' },
  { key: 'type', label: 'Damage Type' },
  { key: 'area', label: 'Damage Area' },
  { key: 'date', label: 'Damage Date' },
  { key: 'description', label: 'Description' },
  { key: 'location', label: 'Damage Location' },
  { key: 'repaired', label: 'Repaired' },
  { key: 'paid', label: 'Paid' },
  { key: 'cost', label: 'Repair Cost' },
  { key: 'operator', label: 'Entered By' },
  { key: 'claim', label: 'Claim #' },
  { key: 'vendor', label: 'Vendor' },
  { key: 'fleet', label: 'Fleet' },
  { key: 'reservation', label: 'Reservation #' },
  { key: 'entered_date', label: 'Date Entered' },
];

const AGE_OPTIONS = [
  { key: 0, value: 0, text: 'All time (no filter)' },
  { key: 7, value: 7, text: 'Last 7 days' },
  { key: 14, value: 14, text: 'Last 14 days' },
  { key: 30, value: 30, text: 'Last 30 days' },
  { key: 60, value: 60, text: 'Last 60 days' },
  { key: 90, value: 90, text: 'Last 90 days' },
  { key: 180, value: 180, text: 'Last 6 months' },
  { key: 365, value: 365, text: 'Last year' },
];

const RcmDamageConfig = React.memo(({ config, accessToken, isLoading, onConfigUpdate }) => {
  const settings = config?.settings || {};
  const dc = settings.damageConfig || {};

  const [allowedTypes, setAllowedTypes] = useState(dc.allowedTypes || []);
  const [allowedAreas, setAllowedAreas] = useState(dc.allowedAreas || []);
  const [maxAgeDays, setMaxAgeDays] = useState(dc.maxAgeDays || 0);
  const [visibleColumns, setVisibleColumns] = useState(
    dc.visibleColumns || ['id', 'type', 'area', 'date', 'description']
  );

  const [availableTypes, setAvailableTypes] = useState([]);
  const [availableAreas, setAvailableAreas] = useState([]);
  const [isLoadingMeta, setIsLoadingMeta] = useState(false);
  const [metaError, setMetaError] = useState(null);

  useEffect(() => {
    if (!accessToken || !config?.isConfigured) return;

    let cancelled = false;
    setIsLoadingMeta(true);
    setMetaError(null);

    fetch(`${Config.SERVER_BASE_URL}/api/integrations/rcm/damage-meta`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(res => res.json())
      .then(data => {
        if (cancelled) return;
        const types = (data.damageTypes || []).map(t => (typeof t === 'string' ? t : t.name));
        const areas = (data.damageAreas || []).map(a => (typeof a === 'string' ? a : a.name));
        setAvailableTypes(types);
        setAvailableAreas(areas);
      })
      .catch(err => {
        if (!cancelled) setMetaError(err.message);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingMeta(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, config?.isConfigured]);

  useEffect(() => {
    const newDc = config?.settings?.damageConfig || {};
    setAllowedTypes(newDc.allowedTypes || []);
    setAllowedAreas(newDc.allowedAreas || []);
    setMaxAgeDays(newDc.maxAgeDays || 0);
    setVisibleColumns(newDc.visibleColumns || ['id', 'type', 'area', 'date', 'description']);
  }, [config?.settings?.damageConfig]);

  const handleTypeToggle = useCallback(type => {
    setAllowedTypes(prev => (prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]));
  }, []);

  const handleAreaToggle = useCallback(area => {
    setAllowedAreas(prev => (prev.includes(area) ? prev.filter(a => a !== area) : [...prev, area]));
  }, []);

  const handleColumnToggle = useCallback(col => {
    setVisibleColumns(prev => {
      if (prev.includes(col)) {
        if (prev.length <= 1) return prev;
        return prev.filter(c => c !== col);
      }
      return [...prev, col];
    });
  }, []);

  const handleSave = useCallback(() => {
    onConfigUpdate({
      damageConfig: {
        allowedTypes,
        allowedAreas,
        maxAgeDays,
        visibleColumns,
      },
    });
  }, [allowedTypes, allowedAreas, maxAgeDays, visibleColumns, onConfigUpdate]);

  const hasActiveFilters = allowedTypes.length > 0 || allowedAreas.length > 0 || maxAgeDays > 0;

  return (
    <Segment>
      <Header as="h4" style={{ marginBottom: '15px' }}>
        <Icon name="wrench" />
        <Header.Content>
          Damage Display Settings
          <Header.Subheader>
            Filter and customize which damage records appear on cards
          </Header.Subheader>
        </Header.Content>
      </Header>

      <Form>
        <Form.Field>
          <label>Max Damage Age</label>
          <Form.Select
            options={AGE_OPTIONS}
            value={maxAgeDays}
            onChange={(e, { value }) => setMaxAgeDays(value)}
            disabled={isLoading}
          />
          <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>
            Only show damages created within this time window
          </small>
        </Form.Field>

        <Divider />

        <Form.Field>
          <label>
            Damage Types
            {isLoadingMeta && <Loader active inline size="mini" style={{ marginLeft: 8 }} />}
          </label>
          {metaError && (
            <Message size="mini" warning>
              Could not load types from RCM: {metaError}
            </Message>
          )}
          {allowedTypes.length === 0 && !isLoadingMeta && (
            <Label basic color="grey" size="small" style={{ marginBottom: '8px' }}>
              All types shown (no filter)
            </Label>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
            {availableTypes.map(type => (
              <Checkbox
                key={type}
                label={type}
                checked={allowedTypes.includes(type)}
                onChange={() => handleTypeToggle(type)}
                disabled={isLoading}
              />
            ))}
            {!isLoadingMeta && availableTypes.length === 0 && !metaError && (
              <span style={{ color: '#999', fontStyle: 'italic' }}>
                No damage types found. Import a vehicle with damages first.
              </span>
            )}
          </div>
          <small style={{ color: '#666' }}>
            Leave all unchecked to show every damage type. Check specific types to filter.
          </small>
        </Form.Field>

        <Form.Field>
          <label>
            Damage Areas
            {isLoadingMeta && <Loader active inline size="mini" style={{ marginLeft: 8 }} />}
          </label>
          {allowedAreas.length === 0 && !isLoadingMeta && (
            <Label basic color="grey" size="small" style={{ marginBottom: '8px' }}>
              All areas shown (no filter)
            </Label>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
            {availableAreas.map(area => (
              <Checkbox
                key={area}
                label={area}
                checked={allowedAreas.includes(area)}
                onChange={() => handleAreaToggle(area)}
                disabled={isLoading}
              />
            ))}
            {!isLoadingMeta && availableAreas.length === 0 && !metaError && (
              <span style={{ color: '#999', fontStyle: 'italic' }}>
                No damage areas found. Import a vehicle with damages first.
              </span>
            )}
          </div>
          <small style={{ color: '#666' }}>
            Leave all unchecked to show every damage area. Check specific areas to filter.
          </small>
        </Form.Field>

        <Divider />

        <Form.Field>
          <label>Visible Columns</label>
          <small style={{ color: '#666', display: 'block', marginBottom: '8px' }}>
            Select which columns appear in the damage table on cards
          </small>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: '6px',
            }}
          >
            {ALL_COLUMNS.map(col => (
              <Checkbox
                key={col.key}
                label={col.label}
                checked={visibleColumns.includes(col.key)}
                onChange={() => handleColumnToggle(col.key)}
                disabled={isLoading}
              />
            ))}
          </div>
        </Form.Field>

        {hasActiveFilters && (
          <div
            style={{
              padding: '10px',
              background: '#f0f7ff',
              borderRadius: '4px',
              border: '1px solid #d0e3f7',
              marginBottom: '12px',
            }}
          >
            <Icon name="filter" color="blue" />
            <strong>Active filters: </strong>
            {maxAgeDays > 0 && (
              <Label size="tiny" color="blue">
                Last {maxAgeDays} days
              </Label>
            )}{' '}
            {allowedTypes.map(t => (
              <Label key={`t-${t}`} size="tiny" basic color="blue">
                Type: {t}
              </Label>
            ))}
            {allowedAreas.map(a => (
              <Label key={`a-${a}`} size="tiny" basic color="teal">
                Area: {a}
              </Label>
            ))}
          </div>
        )}

        <Button
          primary
          size="small"
          onClick={handleSave}
          loading={isLoading}
          disabled={isLoading}
          icon="save"
          content="Save Damage Settings"
        />
      </Form>
    </Segment>
  );
});

RcmDamageConfig.displayName = 'RcmDamageConfig';

export default RcmDamageConfig;
